import { LanguageOptions } from '../types';
/*
 * Maps filenames and file extensions to the correct
 * prism.js and monaco language.
 *
 * Cobbled together from LANGUAGE_EXTENSIONS in Bitbucket:
 *
 * https://staging.bb-inf.net/bitbucket/bitbucket/src/staging/apps/lib/languages.py

 * and the list of supported prism.js languages:
 *
 * http://prismjs.com/#languages-list
 *
 * and the list of supported monaco languages:
 *
 * https://github.com/Microsoft/monaco-languages
 *
 */

const NH = 'plaintext';
export const NO_HIGHLIGHT = NH;

// prettier-ignore
export const languages: { [language: string] : LanguageOptions } = {
  abnf: {
    extensions: ['.abnf'],
    vendorOverride: {
      prism: NH,
      monaco: NH,
    },
  },
  ada: {
    extensions: ['.ada', '.adb', '.ads'],
    vendorOverride: {
      monaco: NH,
    },
  },
  asciidoc: {
    extensions: ['.adoc'],
    vendorOverride: {
      monaco: NH,
    },
  },
  apacheconf: {
    extensions: ['.htaccess'],
    filenames: ['apache.conf', 'apache2.conf'],
    vendorOverride: {
      monaco: NH,
    },
  },
  applescript: {
    extensions: ['.applescript'],
    vendorOverride: {
      monaco: NH,
    },
  },
  actionscript: {
    extensions: ['.as'],
    vendorOverride: {
      monaco: NH,
    },
  },
  autoit: {
    extensions: ['.au3'],
    vendorOverride: {
      monaco: NH,
    },
  },
  latex: {
    extensions: ['.aux', '.tex', '.toc'],
    vendorOverride: {
      monaco: NH,
    },
  },
  brainfuck: {
    extensions: ['.b', '.bf'],
    vendorOverride: {
      monaco: NH,
    },
  },
  bash: {
    extensions: ['.bash', '.ebuild', '.eclass', '.ksh', '.sh'],
    vendorOverride: {
      monaco: NH,
    },
  },
  batch: {
    extensions: ['.bat'],
  },
  c: {
    extensions: ['.c'],
  },
  cpp: {
    // Including .h for the cpp highlighter since these files may be C++ files,
    // and incorrectly highlighting C as C++ is expected to be less disruptive
    // than incorrectly highlighting C++ as C.
    extensions: ['.c++', '.cc', '.cpp', '.cxx', '.h++', '.hh', '.hpp', '.hxx', '.h'],
  },
  ceylon: {
    extensions: ['.ceylon'],
    vendorOverride: {
      prism: NH,
      monaco: NH,
    },
  },
  clojure: {
    extensions: ['.clj'],
    vendorOverride: {
      monaco: NH,
    },
  },
  cmake: {
    extensions: ['.cmake'],
    vendorOverride: {
      prism: 'makefile',
      monaco: NH,
    },
  },
  coffeescript: {
    extensions: ['.coffee'],
    vendorOverride: {
      monaco: 'coffee',
    },
  },
  csharp: {
    extensions: ['.cs'],
  },
  css: {
    extensions: ['.css'],
  },
  d: {
    extensions: ['.d', '.di'],
    vendorOverride: {
      monaco: NH,
    },
  },
  dart: {
    extensions: ['.dart'],
    vendorOverride: {
      monaco: NH,
    },
  },
  diff: {
    extensions: ['.diff', '.patch'],
    vendorOverride: {
      monaco: NH,
    },
  },
  dockerfile: {
    extensions: ['.dockerfile'],
    filenames: ['Dockerfile'],
    vendorOverride: {
      prism: 'docker',
    },
  },
  erlang: {
    extensions: ['.erl', '.erl-sh', '.hrl'],
    vendorOverride: {
      monaco: NH,
    },
  },
  elixir: {
    extensions: ['.ex'],
    vendorOverride: {
      monaco: NH,
    },
  },
  excel: {
    extensions: ['.xls', '.xlsb', '.xlsm', '.xlsx'],
    filenames: [],
    vendorOverride: {
      prism: 'markup',
      monaco: NH,
    },
  },
  fortran: {
    extensions: ['.f', '.f90'],
    vendorOverride: {
      monaco: NH,
    },
  },
  gherkin: {
    extensions: ['.feature'],
    vendorOverride: {
      monaco: NH,
    },
  },
  glsl: {
    extensions: ['.frag', '.geo', '.vert'],
    vendorOverride: {
      monaco: NH,
    },
  },
  fsharp: {
    extensions: ['.fs', '.fsi', '.fsscript', '.fsx'],
  },
  go: {
    extensions: ['.go'],
  },
  groovy: {
    extensions: ['.groovy'],
    vendorOverride: {
      monaco: NH,
    },
  },
  haml: {
    extensions: ['.haml'],
    vendorOverride: {
      monaco: NH,
    },
  },
  haskell: {
    extensions: ['.hs'],
    vendorOverride: {
      monaco: NH,
    },
  },
  handlebars: {
    extensions: ['.handlebars', '.hbs'],
  },
  html: {
    extensions: ['.htm', '.html', '.vue'],
    vendorOverride: {
      prism: 'markup',
    },
  },
  hcl: {
    extensions: ['.tf'],
    vendorOverride: {
      monaco: NH,
    }
  },
  haxe: {
    extensions: ['.hx'],
    vendorOverride: {
      monaco: NH,
    },
  },
  ini: {
    extensions: ['.cfg', '.ini', '.properties'],
  },
  java: {
    extensions: ['.java', '.jsp'],
  },
  javascript: {
    extensions: ['.js', '.jsx'],
    vendorOverride: {
      prism: 'jsx',
    },
  },
  json: {
    extensions: ['.json'],
  },
  kotlin: {
    extensions: ['.kt', '.kts'],
    vendorOverride: {
      monaco: NH,
    },
  },
  lasso: {
    extensions: ['.lasso', '.las', '.lassoapp'],
    vendorOverride: {
      prism: NH,
      monaco: NH,
    },
  },
  less: {
    extensions: ['.less'],
  },
  lisp: {
    extensions: ['.el', '.cl', '.lisp'],
    vendorOverride: {
      monaco: NH,
    },
  },
  llvm: {
    extensions: ['.ll'],
    vendorOverride: {
      prism: NH,
      monaco: NH,
    },
  },
  livescript: {
    extensions: ['.ls'],
    vendorOverride: {
      monaco: NH,
    },
  },
  lua: {
    extensions: ['.lua'],
  },
  makefile: {
    extensions: ['.gnumakefile', '.mak', '.makefile'],
    filenames: ['Makefile'],
    vendorOverride: {
      monaco: NH,
    },
  },
  markdown: {
    extensions: ['.md', '.mkd', '.mkdown'],
  },
  msdax: {
    extensions: ['.dax', '.msdax'],
    vendorOverride: {
      prism: NH,
    },
  },
  objectivec: {
    extensions: ['.m'],
    vendorOverride: {
      monaco: 'objective-c',
    },
  },
  ocaml: {
    extensions: ['.ml', '.mli', '.mll', '.mly'],
    vendorOverride: {
      monaco: NH,
    },
  },
  nimrod: {
    extensions: ['.nim'],
    vendorOverride: {
      prism: NH,
      monaco: NH,
    },
  },
  pascal: {
    extensions: ['.pas'],
    vendorOverride: {
      monaco: NH,
    },
  },
  perl: {
    extensions: ['.pl', '.pm'],
    vendorOverride: {
      monaco: NH,
    },
  },
  php: {
    extensions: ['.php', '.php3', '.php4', '.php5'],
  },
  plaintext: {
    extensions: ['.txt'],
    vendorOverride: {
      prism: NH,
    },
  },
  postiats: {
    extensions: ['.dats', '.sats', '.hats'],
    vendorOverride: {
      prism: NH,
    },
  },
  powershell: {
    extensions: ['.ps1', '.psm1', '.psd1'],
  },
  protobuf: {
    extensions: ['.foo'],
    vendorOverride: {
      monaco: NH,
    },
  },
  prolog: {
    extensions: ['.pro', '.prolog'],
    vendorOverride: {
      monaco: NH,
    },
  },
  pug: {
    extensions: ['.jade', '.pug'],
  },
  python: {
    extensions: ['.py', '.pyw'],
  },
  qml: {
    extensions: ['.qml'],
  },
  r: {
    extensions: ['.r', '.rdata', '.rda', '.rds'],
  },
  razor: {
    extensions: ['.cshtml'],
    vendorOverride: {
      prism: NH,
    },
  },
  rego: {
    extensions: ['.rego'],
    vendorOverride: {
      prism: 'rego',
      monaco: NH,
    },
  },
  ruby: {
    extensions: ['.rake', '.rb', '.rbw', '.rbx'],
    filenames: ['.gemspec', 'Rakefile'],
  },
  rust: {
    extensions: ['.rs'],
    vendorOverride: {
      monaco: NH,
    },
  },
  restructuredtext: {
    extensions: ['.rst'],
    filenames: [],
    vendorOverride: {
      prism: 'rest',
      monaco: NH,
    },
  },
  sass: {
    extensions: ['.sass'],
    vendorOverride: {
      monaco: 'scss',
    },
  },
  sb: {
    extensions: ['.sb'],
    vendorOverride: {
      prism: NH,
    },
  },
  scss: {
    extensions: ['.scss'],
  },
  scala: {
    extensions: ['.scala'],
    vendorOverride: {
      monaco: NH,
    },
  },
  scheme: {
    extensions: ['.scm'],
    vendorOverride: {
      monaco: NH,
    },
  },
  sol: {
    extensions: ['.sol'],
    vendorOverride: {
      prism: NH,
    },
  },
  sql: {
    extensions: ['.sql', '.hql'],
  },
  smalltalk: {
    extensions: ['.st'],
    vendorOverride: {
      monaco: NH,
    },
  },
  stylus: {
    extensions: ['.styl'],
    vendorOverride: {
      monaco: NH,
    },
  },
  swift: {
    extensions: ['.swift'],
  },
  tcl: {
    extensions: ['.tcl'],
    vendorOverride: {
      monaco: NH,
    },
  },
  tsx: {
    extensions: ['.tsx'],
    vendorOverride: {
      monaco: 'typescript',
    },
  },
  typescript: {
    extensions: ['.ts'],
  },
  verilog: {
    extensions: ['.v'],
    vendorOverride: {
      monaco: NH,
    },
  },
  vala: {
    extensions: ['.vala', '.vapi'],
    vendorOverride: {
      monaco: NH,
      prism: NH,
    },
  },
  xml: {
    extensions: ['.xml', '.xsd', '.rss', '.wsdl'],
    vendorOverride: {
      prism: 'markup',
    },
  },
  vb: {
    extensions: ['.vbe', '.vbs', '.wsc', '.wsf'],
    vendorOverride: {
      prism: 'vbnet',
    },
  },
  vbnet: {
    extensions: ['.bas', '.vb'],
    vendorOverride: {
      monaco: 'vb',
    },
  },
  vim: {
    extensions: ['.vim'],
    filenames: ['.vimrc'],
    vendorOverride: {
      monaco: NH,
    },
  },
  yaml: {
    extensions: ['.yaml', '.yml'],
  },
};
